import { useState, useEffect } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

function collectIds(node, out = {}) {
    if (!node) return out;
    out[node.id] = true;
    (node.children || []).forEach(c => collectIds(c, out));
    return out;
}

/* ─── Root node card ─────────────────────────────────────────────── */
function RootCard({ node, hasKids, isOpen, onToggle }) {
    return (
        <div className="bg-gradient-to-br from-[#0C2B5C] to-[#1B3A7A] text-white
            px-3 py-2.5 rounded-xl shadow-lg border-b-2 border-[#2172E5] w-36 select-none cursor-default">
            <div className="flex items-center justify-between mb-1">
                <span className="text-[8px] font-bold tracking-widest text-amber-300/70 uppercase">Root</span>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0
                    ${node.status === "active" ? "bg-emerald-400" : "bg-slate-400"}`} />
            </div>
            <div className="font-mono font-bold text-sm text-amber-300 leading-tight tracking-wide">
                {node.referral_code || "—"}
            </div>
            {node.email && (
                <div className="text-[9px] text-slate-300/80 truncate mt-0.5 leading-tight">{node.email}</div>
            )}
            <div className="flex justify-between border-t border-white/10 mt-2 pt-1.5 text-[9px] font-bold">
                <span className="text-teal-300">L:{node.left_active_count}</span>
                <span className="text-rose-300">R:{node.right_active_count}</span>
            </div>
            {hasKids && (
                <button onClick={onToggle}
                    className="mt-1.5 w-full text-[8px] text-white/30 hover:text-white/70 transition-colors text-center">
                    {isOpen ? "▲" : "▼ show"}
                </button>
            )}
        </div>
    );
}

/* ─── Child node card ────────────────────────────────────────────── */
function ChildCard({ node, depth, hasKids, isOpen, onToggle }) {
    const isLeft = node.side === "L";

    return (
        <div className={`bg-white px-3 py-2 rounded-xl w-36 select-none cursor-default
            hover:shadow-sm transition-all duration-150
            ${isLeft ? "border border-teal-400/70" : "border border-rose-400/70"}`}>

            <div className="flex items-center justify-between mb-1">
                <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded
                    ${isLeft
                        ? "text-teal-700 bg-teal-50"
                        : "text-rose-600 bg-rose-50"
                    }`}>
                    {isLeft ? `←L${depth}` : `R${depth}→`}
                </span>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0
                    ${node.status === "active" ? "bg-emerald-400" : "bg-slate-300"}`} />
            </div>

            <div className={`font-mono font-bold text-sm leading-tight tracking-wide
                ${isLeft ? "text-teal-700" : "text-rose-600"}`}>
                {node.referral_code || "—"}
            </div>

            {node.email ? (
                <div className="text-[9px] text-slate-400 truncate mt-0.5 leading-tight">{node.email}</div>
            ) : (
                <div className="text-[9px] text-slate-200 mt-0.5 leading-tight italic">no email</div>
            )}

            <div className="flex justify-between border-t border-slate-100 mt-1.5 pt-1 text-[9px] font-semibold">
                <span className="text-teal-500">L:{node.left_active_count}</span>
                <span className="text-rose-400">R:{node.right_active_count}</span>
            </div>

            {hasKids && (
                <button onClick={onToggle}
                    className="mt-1 w-full text-[8px] text-slate-300 hover:text-slate-600 transition-colors text-center">
                    {isOpen ? "▲" : "▼ show"}
                </button>
            )}
        </div>
    );
}

/* ─── Connector line ─────────────────────────────────────────────── */
function VStem({ color = "bg-slate-200", h = "h-5" }) {
    return <div className={`w-px ${h} ${color} shrink-0`} />;
}

/* ─── Recursive tree node ────────────────────────────────────────── */
function TreeNode({ node, depth, expanded, toggle }) {
    const isOpen  = expanded[node.id] ?? true;
    const isRoot  = depth === 0;
    const left    = (node.children || []).filter(c => c.side === "L");
    const right   = (node.children || []).filter(c => c.side === "R");
    const hasKids = left.length > 0 || right.length > 0;
    const both    = left.length > 0 && right.length > 0;

    const card = isRoot
        ? <RootCard  node={node} hasKids={hasKids} isOpen={isOpen} onToggle={() => toggle(node.id)} />
        : <ChildCard node={node} depth={depth} hasKids={hasKids} isOpen={isOpen} onToggle={() => toggle(node.id)} />;

    return (
        <div className="flex flex-col items-center">
            {card}

            {hasKids && isOpen && (
                <div className="flex flex-col items-center w-full">
                    {/* Stem down from card */}
                    <VStem h="h-5" />

                    {/* Horizontal cross-bar when both wings exist */}
                    {both && (
                        <div className="relative w-full flex justify-center h-px">
                            <div className="absolute top-0 left-[25%] right-[25%] h-px bg-slate-200" />
                        </div>
                    )}

                    {/* Wing columns */}
                    <div className={`flex items-start ${both ? "gap-6" : ""}`}>

                        {/* LEFT */}
                        {left.length > 0 && (
                            <div className="flex flex-col items-center">
                                <VStem h={both ? "h-5" : "h-3"} color="bg-teal-200" />
                                <div className="flex flex-col items-center gap-0">
                                    {left.map(child => (
                                        <div key={child.id} className="flex flex-col items-center">
                                            <VStem h="h-4" color="bg-teal-100" />
                                            <TreeNode node={child} depth={depth + 1} expanded={expanded} toggle={toggle} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* RIGHT */}
                        {right.length > 0 && (
                            <div className="flex flex-col items-center">
                                <VStem h={both ? "h-5" : "h-3"} color="bg-rose-200" />
                                <div className="flex flex-col items-center gap-0">
                                    {right.map(child => (
                                        <div key={child.id} className="flex flex-col items-center">
                                            <VStem h="h-4" color="bg-rose-100" />
                                            <TreeNode node={child} depth={depth + 1} expanded={expanded} toggle={toggle} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Public export ──────────────────────────────────────────────── */
export default function TreeView({ root }) {
    const [expanded, setExpanded] = useState({});
    const [scale,    setScale]    = useState(1);

    useEffect(() => {
        if (root) setExpanded(collectIds(root));
    }, [root]);

    if (!root) return null;

    const toggle = (id) =>
        setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    return (
        <div className="relative flex flex-col" style={{ minHeight: 520 }}>

            {/* Zoom controls */}
            <div className="absolute top-3 left-3 z-10 bg-white/95 border border-slate-100
                shadow-md p-1.5 rounded-xl flex items-center gap-0.5">
                <button
                    onClick={() => setScale(s => Math.min(+(s + 0.1).toFixed(1), 2.0))}
                    className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors"
                    title="Zoom in">
                    <ZoomIn className="w-3.5 h-3.5" />
                </button>
                <button
                    onClick={() => setScale(s => Math.max(+(s - 0.1).toFixed(1), 0.3))}
                    className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors"
                    title="Zoom out">
                    <ZoomOut className="w-3.5 h-3.5" />
                </button>
                <button
                    onClick={() => setScale(1)}
                    className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors"
                    title="Reset zoom">
                    <Maximize2 className="w-3.5 h-3.5" />
                </button>
                <div className="h-3 w-px bg-slate-200 mx-0.5" />
                <span className="text-[10px] font-bold text-slate-400 px-1 tabular-nums w-8 text-center">
                    {Math.round(scale * 100)}%
                </span>
            </div>

            {/*
                Scrollable canvas.
                - outer: overflow-auto so both axes scroll
                - inner wrapper: width + height set from scaled content via inline style
                  so the scroll area grows when zoomed in
                - tree div: transform scale with top-left origin so scroll follows zoom correctly
            */}
            <div
                className="overflow-auto flex-1"
                style={{ minHeight: 520 }}
            >
                <div style={{ padding: "4rem 5rem", width: "max-content", minWidth: "100%" }}>
                    <div
                        style={{
                            display: "inline-block",
                            transform: `scale(${scale})`,
                            transformOrigin: "top left",
                            transition: "transform 0.15s ease",
                        }}
                    >
                        <TreeNode node={root} depth={0} expanded={expanded} toggle={toggle} />
                    </div>
                </div>
            </div>
        </div>
    );
}
